/* eslint-disable no-console */
/* eslint-disable max-len */
import {CrownFilled} from '@ant-design/icons';
import {Layout, theme, Flex, Breadcrumb} from 'antd';
import React, {useEffect, useMemo, useState} from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';

import {breakCamelCaseWithNumbers} from '../../../components/DisplayModal/Utils/breakCamelCase';
import NotFoundPage from '../../../components/ErrorHandling/NotFoundComp/NotFound';
import Loader from '../../../components/Loader/Loader';
import {
  allRoutes
  // Routes
} from '../../../constants/Routes';
import TranslatedText from '../../../libraries/I18N/TranslatedText';
import pillarsAPI from '../../../services/modules/ESG/pillarsAPI';
// import checkSelectedYear from '../../../shared/checkSelectedYear';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import useJSONStore from '../../../store/StaticJson/jsonStore';
import {checkCurrentYearOrBelow} from '../../../utils/commonFunctions';
import {FirstCap} from '../../../utils/FormUtils/FirstCap';
import {
  hasAccessToReport,
  shouldBlockReportAccess
} from '../../../utils/SubscriptionHelper';
import {
  addPillarsToFrameworkObj,
  filterPillarsData,
  handleIsComingSoonConditionally
} from '../constants/commonFunctions';
import {frameworkRoute} from '../constants/frameworkRoutes';
import {FrameworkObj} from '../constants/FrameWorksConst';
import {returnQuestions} from '../constants/returnQuestions';

import Questionnaire from './Questionnaire';
import QuestionnaireHead from './QuestionnaireHead';
import QuestionnaireTabs from './QuestionnaireTabs';

const {Content} = Layout;

const Questions: React.FC = () => {
  const [QTabs, setQtabs] = useState<any[]>([]);
  const [pillarObj, setPillarObj] = useState<any>();
  const [hasResolvedPillarObj, setHasResolvedPillarObj] = useState(false);

  const {framework, pillar} = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedYear = checkCurrentYearOrBelow(
    searchParams.get('selectedYear')
  );

  const [isPageReady, setIsPageReady] = useState(false);
  const [defaultKey, setDefaultKey] = useState('');
  const [filteredFramework, setFilteredFramework] = useState<any[]>([]);
  const [questionData, setQuestionData] = useState({});
  const [isPillarLoaded, setIsPillarLoaded] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const companyData = useCompanyStore?.getState().companyDetails;

  const esgPillarsData = useJSONStore?.getState().esgPillars;
  const isLocationStateFalsy = !location?.state;

  useEffect(() => {
    if (selectedYear) {
      searchParams.set('selectedYear', selectedYear);
      setSearchParams(searchParams);
      if (isLocationStateFalsy) {
        setIsPillarLoaded(false);
        pillarsAPI(selectedYear, true, framework)
          .then(() => {})
          .catch(() => {})
          .finally(() => {
            setIsPillarLoaded(true);
          });
      }
    }
  }, [selectedYear]);

  const {
    token: {colorBgContainer, borderRadiusLG}
  } = theme.useToken();

  useEffect(() => {
    const filteredFrameworkData = addPillarsToFrameworkObj(
      useJSONStore?.getState().esgPillars
    );
    setFilteredFramework(filteredFrameworkData);

    setHasResolvedPillarObj(false);
    setPillarObj(undefined);
    returnQuestions(framework, pillar, setPillarObj, companyData?.subCategory);
    setHasResolvedPillarObj(true);
  }, [framework, pillar, companyData?.subCategory]);

  const filteredPillarData = useMemo(() => {
    const frFil =
      FrameworkObj?.find((item) => {
        return item?.route === framework;
      })?.timeline ?? [];

    return frFil?.filter((i: any) => {
      // Filter by category if provided
      const categoryMatch = i?.category
        ? i?.category === companyData?.category
        : true;

      // Filter by subCategory - for CBAM, only show items matching the current subCategory
      let subCategoryMatch = true;
      if (i?.subCategory !== undefined && i?.subCategory !== null) {
        subCategoryMatch =
          companyData?.subCategory !== undefined &&
          companyData?.subCategory !== null &&
          i?.subCategory === companyData?.subCategory;
      }

      return categoryMatch && subCategoryMatch;
    });
  }, [framework, companyData?.subCategory]);

  useEffect(() => {
    setQuestionData(
      location?.state ??
        filterPillarsData(esgPillarsData, framework ?? '', pillar ?? '')
    );
    setDefaultKey(pillarObj?.tabs?.[0]?.id ?? '');
    setQtabs(
      pillarObj?.tabs.map((item: any, index: number) => {
        return {
          id: item.id,
          name: <TranslatedText translationKey={item.title} />,
          component: (
            <Questionnaire
              tabId={item?.id}
              principleTitle={item?.principleTitle}
              setDefaultKey={setDefaultKey}
              nextTabId={pillarObj?.tabs?.[index + 1]?.id ?? ''}
              items={item.questions}
              accordion={item.accordion}
              APIRoute={pillarObj?.APIRoute ?? ''}
              setIsSubmitted={setIsSubmitted}
            />
          )
        };
      })
    );
  }, [pillarObj]);

  useEffect(() => {
    if (isLocationStateFalsy) {
      const ready = pillarObj && QTabs && isPillarLoaded;

      setIsPageReady(!!ready);
    }
  }, [pillarObj, QTabs, questionData, isPillarLoaded]);

  useEffect(() => {
    setQuestionData(
      filterPillarsData(esgPillarsData, framework ?? '', pillar ?? '')
    );
    if (isSubmitted) {
      setFilteredFramework(addPillarsToFrameworkObj(esgPillarsData));
      setIsSubmitted(false);
    }
  }, [esgPillarsData, isSubmitted]);

  return !hasAccessToReport(framework) ||
    shouldBlockReportAccess(framework) ||
    handleIsComingSoonConditionally(framework as frameworkRoute, false) ? (
    <NotFoundPage />
  ) : hasResolvedPillarObj &&
    isPillarLoaded &&
    framework &&
    pillar &&
    !pillarObj ? (
    <NotFoundPage />
  ) : !isPageReady ? (
    <Loader />
  ) : (
    <div className="page">
      <Breadcrumb
        items={[
          {
            title: (
              <div
                onClick={() => {
                  navigate({
                    pathname: `${allRoutes.ESG.FRAMEWORK}`,
                    search: location.search
                  });
                }}
                style={{
                  cursor: 'pointer'
                }}
              >
                Home
              </div>
            )
          },
          {
            title: (
              <div
                onClick={() => {
                  navigate(
                    `${allRoutes.ESG.FRAMEWORK}/${framework}${location.search}`,
                    {
                      state: filteredFramework?.find((item) => {
                        return item?.route === framework;
                      })?.pillars
                    }
                  );
                }}
                style={{
                  cursor: 'pointer'
                }}
              >
                {FrameworkObj?.filter((i) => hasAccessToReport(i?.route))?.find(
                  (item) => {
                    return item?.route === framework;
                  }
                )?.shortTitle ?? framework}
              </div>
            ),
            // dropdownProps: {trigger: ['click']},
            menu: {
              items: filteredFramework.map((item) => {
                if (shouldBlockReportAccess(item.route)) {
                  return {
                    title: (
                      <>
                        {item.route}
                        <CrownFilled
                          style={{color: '#FFD700', paddingLeft: 10}}
                        />
                      </>
                    )
                  };
                }

                return {
                  title: (
                    <Link
                      to={`${allRoutes.ESG.FRAMEWORK}/${item.route}${location.search}`}
                      replace={true}
                      state={item?.pillars}
                    >
                      {item?.shortTitle ?? item?.route}
                    </Link>
                  )
                };
              }),
              getPopupContainer: (triggerNode: any) => triggerNode.parentNode
            },
            dropdownProps: {placement: 'bottomLeft'}
          },
          {
            title: (
              <div
                onClick={() => {
                  navigate({
                    pathname: `${allRoutes.ESG.FRAMEWORK}/${framework}/${pillar}`,
                    search: location.search
                  });
                }}
                style={{
                  cursor: 'pointer'
                }}
              >
                {filteredPillarData?.find((item: any) => {
                  return (
                    item?.routes?.toLowerCase().split('/')?.[1] ===
                    pillar?.toLowerCase()
                  );
                })?.shortTitle ??
                  breakCamelCaseWithNumbers(FirstCap(pillar ?? ''))}
              </div>
            ),
            // dropdownProps: {trigger: ['click']},
            menu: {
              items: filteredPillarData?.map((item: any) => {
                return {
                  title: (
                    <Link
                      to={`${allRoutes.ESG.FRAMEWORK}/${item?.routes}${location.search}`}
                      replace={true}
                      state={item?.pillars}
                    >
                      {item?.shortTitle ?? item?.ptitle}
                    </Link>
                  )
                };
              }),
              getPopupContainer: (triggerNode: any) => triggerNode.parentNode
            },
            dropdownProps: {placement: 'bottomLeft'}
          }
        ]}
        separator="/"
        style={{width: 'max-content', marginBottom: '4px'}}
      />
      <Flex vertical gap="20px">
        <Flex>
          <Content
            style={{
              // padding: 24,
              margin: 0,
              minHeight: 100,
              maxHeight: 240,
              width: '100%',
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
              boxShadow: '0px 3px 6px #E0EFFD',
              overflow: 'hidden'
            }}
          >
            <QuestionnaireHead
              headDetails={pillarObj?.header ?? ''}
              data={questionData}
            />
          </Content>
        </Flex>
        <Flex>
          <Content
            style={{
              padding: 24,
              margin: 0,
              minHeight: 150,
              width: '100%',
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
              boxShadow: '0px 3px 6px #E0EFFD'
            }}
          >
            <QuestionnaireTabs
              defaultKey={defaultKey}
              QTabs={QTabs}
              setDefaultKey={setDefaultKey}
            />
          </Content>
        </Flex>
      </Flex>
    </div>
  );
};

export default Questions;
